import { monotonicNow, wallNow } from '@casa/util';
import type { AccessUnit } from '../h264/h264_depacketizer.ts';
import { accessUnitOf, parseFragments, parseInit, type VideoTrack } from '../hls/fmp4.ts';
import { isMasterPlaylist, parseMasterPlaylist, parseMediaPlaylist, resolveUri, type MediaPiece, type MediaPlaylist } from '../hls/m3u8.ts';
import type { StreamEvent, StreamState, StreamStats } from './rtsp_stream.ts';
import { createVideoPipeline, type PipelineMode, type VideoPipeline, type VideoPipelineOptions } from './video_pipeline.ts';

/** A GET through the bridge: the status and the whole body. */
export type HlsFetch = (url: string, timeoutMs: number) => Promise<{ status: number; body: Uint8Array }>;

export interface HlsStreamOptions {
    /** The master or media playlist. */
    url: string;
    fetch: HlsFetch;
    /** With a Worker pipeline the canvas is handed over for good; nothing else may draw on it. */
    canvas: HTMLCanvasElement;
    mode?: PipelineMode | 'auto';
    /** No decoded picture for this long ends the session and starts again. Default 30 s. */
    stallTimeoutMs?: number;
    /** Wait before the first retry; doubled for each further one, up to the maximum. Default 2 s and 15 s. */
    retryDelayMs?: number;
    maxRetryDelayMs?: number;
    /** Further behind the live edge than this, playing starts again at it (VH-4). Default 10 s. */
    maxBehindMs?: number;
    log?: (message: string) => void;
    /** For tests. */
    createPipeline?: (canvas: HTMLCanvasElement, options: VideoPipelineOptions) => Promise<VideoPipeline>;
}

/** Where the next piece is: a segment's media sequence number, and the part in it (low latency). */
interface Position {
    msn: number;
    part: number | undefined;
}

const WATCHDOG_INTERVAL_MS = 500;
const MAX_EVENTS = 50;
/** The first playlist can take a while: Home Assistant starts the camera's stream when asked (VH spec §1). */
const PLAYLIST_TIMEOUT_MS = 30_000;
const PIECE_TIMEOUT_MS = 20_000;
/** A picture more than this late, or this early, sets the pacing clock anew instead of being waited for. */
const RESYNC_MS = 1_000;
const FAR_AHEAD_MS = 10_000;

const text = (error: unknown) : string => (error instanceof Error ? error.message : String(error));
const decoder = new TextDecoder();

/** The pieces a player plays: the parts where there are any (low latency), else the segments. */
const piecesOf = (playlist: MediaPlaylist) : MediaPiece[] => (playlist.parts.length > 0 ? playlist.parts : playlist.segments);

/**
 * Where to start (VH-4): the last independent piece that begins at least the hold-back from the
 * end (`PART-HOLD-BACK`, else three part targets or target durations). Undefined when the
 * playlist has nothing yet.
 */
export function startPosition(playlist: MediaPlaylist) : Position | undefined {
    const pieces = piecesOf(playlist);
    if (pieces.length === 0) {
        return undefined;
    }
    const lowLatency = playlist.parts.length > 0;
    const holdBack = playlist.partHoldBack ?? 3 * (lowLatency ? (playlist.partTarget ?? 1) : playlist.targetDuration);
    let index = pieces.length - 1;
    let behind = pieces[index]!.duration;
    while (index > 0 && behind < holdBack) {
        index--;
        behind += pieces[index]!.duration;
    }
    // The independent piece at or before it; where the playlist lists none that far back (Home
    // Assistant lists only the last segments' parts), the first after it, closer to live.
    const before = pieces.slice(0, index + 1).findLastIndex((piece) => piece.independent);
    const after = pieces.findIndex((piece, at) => at > index && piece.independent);
    const piece = pieces[before !== -1 ? before : after !== -1 ? after : index]!;

    return { msn: piece.msn, part: piece.part };
}

/**
 * The piece at `position` in the playlist; `gone` when the playlist no longer has it (too far
 * behind); undefined when it is not there yet. A part beyond the last of a finished segment is
 * the next segment's first.
 */
export function pieceAt(playlist: MediaPlaylist, position: Position) : MediaPiece | 'gone' | undefined {
    const pieces = piecesOf(playlist);
    const first = pieces[0];
    if (position.part === undefined || playlist.parts.length === 0) {
        const segment = playlist.segments.find((each) => each.msn === position.msn);
        return segment ?? (first && position.msn < first.msn ? 'gone' : undefined);
    }
    const part = playlist.parts.find((each) => each.msn === position.msn && each.part === position.part);
    if (part) {
        return part;
    }
    if (playlist.segments.some((each) => each.msn === position.msn)) {
        return pieceAt(playlist, { msn: position.msn + 1, part: 0 });
    }

    return first && (position.msn < first.msn || (position.msn === first.msn && position.part < (first.part ?? 0))) ? 'gone' : undefined;
}

/** How much of the playlist comes after `piece`, in seconds. */
function behindLive(playlist: MediaPlaylist, piece: MediaPiece) : number {
    const pieces = piecesOf(playlist);
    const index = pieces.indexOf(piece);

    return pieces.slice(index + 1).reduce((sum, each) => sum + each.duration, 0);
}

/**
 * One HLS stream on a canvas that keeps itself alive (docs/specs/video-hls.md): the playlists
 * and pieces fetched through the bridge, the video samples unpacked from fMP4 and paced to the
 * decoder at their own times, a watchdog that starts again when no picture has been decoded for
 * the stall timeout. The same face as `RtspStream`, so that a tile can play either.
 */
export class HlsStream {
    readonly #options: HlsStreamOptions;
    readonly #stallTimeoutMs: number;
    readonly #events: StreamEvent[] = [];
    #state: StreamState = 'stopped';
    #pipeline: VideoPipeline | null = null;
    // Raised whenever the current attempt or session is over; late callbacks compare it.
    #generation = 0;
    #startedAt = 0;
    #watchdog: ReturnType<typeof setInterval> | undefined;
    #retryTimer: ReturnType<typeof setTimeout> | undefined;
    #paceTimer: ReturnType<typeof setTimeout> | undefined;
    #firstOutcome: (() => void) | null = null;
    #session: { startedAt: number; bytes: number; track: NonNullable<StreamStats['session']>['track'] } | null = null;
    #sessionActive = false;
    #sessions = 0;
    #stalls = 0;
    #failures = 0;
    #lastError: string | null = null;
    #framesBefore = 0;
    #framesNow = 0;
    #lastProgressAt = 0;
    #retries = 0;
    /** Pictures waiting for their time, and the clock that sets it (VH-5). */
    #queue: { unit: AccessUnit; decodeTime: number }[] = [];
    #clock: { decodeTime: number; wall: number; timescale: number } | null = null;

    constructor(options: HlsStreamOptions) {
        this.#options = options;
        this.#stallTimeoutMs = options.stallTimeoutMs ?? 30_000;
    }

    /** Starts playing. Resolves when the first attempt has either started playing or failed. */
    async start() : Promise<void> {
        if (this.#state !== 'stopped') {
            return;
        }

        this.#state = 'connecting';
        this.#startedAt = wallNow();
        const generation = ++this.#generation;
        const { mode, log } = this.#options;
        let pipeline: VideoPipeline;
        try {
            pipeline = await (this.#options.createPipeline ?? createVideoPipeline)(this.#options.canvas, {
                ...(mode ? { mode } : {}),
                ...(log ? { log } : {}),
            });
        } catch (error) {
            if (generation === this.#generation) {
                this.#state = 'stopped';
            }
            throw error;
        }
        if (generation !== this.#generation) {
            pipeline.close();

            return;
        }
        this.#pipeline = pipeline;
        this.#watchdog = setInterval(() => this.#watch(), WATCHDOG_INTERVAL_MS);
        const outcome = new Promise<void>((resolve) => {
            this.#firstOutcome = resolve;
        });
        void this.#connect();
        await outcome;
    }

    async stop() : Promise<void> {
        if (this.#state === 'stopped') {
            return;
        }

        this.#generation++;
        clearInterval(this.#watchdog);
        clearTimeout(this.#retryTimer);
        this.#endSession();
        this.#state = 'stopped';
        this.#event('stopped');
        this.#settleFirstOutcome();
        this.#pipeline?.close();
    }

    stats() : StreamStats {
        const pipelineStats = this.#pipeline?.stats() ?? null;
        const firstFrameTime = pipelineStats?.decoder.firstFrameTime ?? null;
        const framesNow = this.#sessionActive ? Math.max(this.#framesNow, pipelineStats?.decoder.framesDecoded ?? 0) : 0;

        return {
            state: this.#state,
            mode: this.#pipeline?.mode ?? null,
            modeFallbackReason: this.#pipeline?.fallbackReason ?? null,
            uptimeMs: this.#startedAt === 0 ? 0 : wallNow() - this.#startedAt,
            sessions: this.#sessions,
            stalls: this.#stalls,
            failures: this.#failures,
            lastError: this.#lastError,
            framesDecodedTotal: this.#framesBefore + framesNow,
            session: this.#session && {
                playingMs: wallNow() - this.#session.startedAt,
                firstFrameMs: firstFrameTime === null ? null : firstFrameTime - this.#session.startedAt,
                // The pieces' bytes, as the RTSP stream counts its packets'.
                rtpBytes: this.#session.bytes,
                track: this.#session.track,
            },
            depacketizer: pipelineStats?.depacketizer ?? null,
            decoder: pipelineStats?.decoder ?? null,
            events: [...this.#events],
        };
    }

    #event(type: StreamEvent['type'], detail?: string) : void {
        this.#events.push({ time: wallNow(), type, ...(detail === undefined ? {} : { detail }) });
        if (this.#events.length > MAX_EVENTS) {
            this.#events.shift();
        }
        this.#options.log?.(`video: ${type}${detail === undefined ? '' : ` (${detail})`}`);
    }

    #settleFirstOutcome() : void {
        this.#firstOutcome?.();
        this.#firstOutcome = null;
    }

    async #get(url: string, timeoutMs: number) : Promise<Uint8Array> {
        const response = await this.#options.fetch(url, timeoutMs);
        if (response.status < 200 || response.status >= 300) {
            throw new Error(`${new URL(url).pathname.split('/').pop() ?? url} answered ${response.status}`);
        }

        return response.body;
    }

    /** The media playlist's address: the master playlist's first H.264 variant, or the address itself. */
    async #mediaPlaylist() : Promise<{ url: string; playlist: MediaPlaylist }> {
        const { url } = this.#options;
        const first = decoder.decode(await this.#get(url, PLAYLIST_TIMEOUT_MS));
        if (!isMasterPlaylist(first)) {
            return { url, playlist: parseMediaPlaylist(first) };
        }
        const variants = parseMasterPlaylist(first);
        const variant = variants.find((each) => each.codecs === undefined || /avc1|avc3/.test(each.codecs)) ?? variants[0];
        if (!variant) {
            throw new Error('the playlist names no stream');
        }
        const media = resolveUri(variant.uri, url);

        return { url: media, playlist: parseMediaPlaylist(decoder.decode(await this.#get(media, PLAYLIST_TIMEOUT_MS))) };
    }

    /** The playlist again: held by the server until `next` is there where it can, else after a wait. */
    async #reload(url: string, playlist: MediaPlaylist, next: Position | undefined, current: () => boolean) : Promise<MediaPlaylist> {
        const lowLatency = playlist.parts.length > 0;
        if (playlist.canBlockReload && next !== undefined) {
            const query = `_HLS_msn=${next.msn}${lowLatency && next.part !== undefined ? `&_HLS_part=${next.part}` : ''}`;
            const held = `${url}${url.includes('?') ? '&' : '?'}${query}`;
            try {
                return parseMediaPlaylist(decoder.decode(await this.#get(held, PLAYLIST_TIMEOUT_MS)));
            } catch (error) {
                if (!current()) {
                    throw error;
                }
                // A server that will not hold this one (too far ahead): ask again plainly, after a wait.
            }
        }
        const waitMs = 1000 * (lowLatency ? (playlist.partTarget ?? 1) : Math.max(1, playlist.targetDuration / 2));
        await new Promise((resolve) => setTimeout(resolve, waitMs));

        return parseMediaPlaylist(decoder.decode(await this.#get(url, PLAYLIST_TIMEOUT_MS)));
    }

    async #connect() : Promise<void> {
        const pipeline = this.#pipeline!;
        const generation = ++this.#generation;
        const current = () : boolean => generation === this.#generation;
        this.#state = 'connecting';
        this.#event('connecting');

        try {
            let { url, playlist } = await this.#mediaPlaylist();
            if (!playlist.map) {
                throw new Error('the stream is not fragmented MP4 (MPEG-TS is not played)');
            }
            const track = parseInit(await this.#get(resolveUri(playlist.map, url), PIECE_TIMEOUT_MS));
            if (!track) {
                throw new Error('the stream has no H.264 video');
            }
            if (!current()) {
                return;
            }
            pipeline.start({ payloadType: 0, clockRate: track.timescale, sps: track.sps, pps: track.pps });
            this.#session = { startedAt: wallNow(), bytes: 0, track: { payloadType: 0, clockRate: track.timescale, packetizationMode: 0, profileLevelId: null } };
            this.#sessionActive = true;
            this.#framesNow = 0;
            this.#lastProgressAt = monotonicNow();
            this.#queue = [];
            this.#clock = null;
            this.#sessions++;
            this.#state = 'playing';
            this.#event('playing');
            this.#settleFirstOutcome();

            const parameters = { sps: track.sps, pps: track.pps };
            let next = startPosition(playlist);
            while (current()) {
                const piece = next === undefined ? undefined : pieceAt(playlist, next);
                if (piece === 'gone') {
                    this.#event('stalled', 'fell out of the playlist; back to live');
                    next = startPosition(playlist);
                    this.#clock = null;
                    continue;
                }
                if (piece === undefined) {
                    if (playlist.ended) {
                        throw new Error('the stream ended');
                    }
                    playlist = await this.#reload(url, playlist, next, current);
                    next ??= startPosition(playlist);
                    continue;
                }
                const bytes = await this.#get(resolveUri(piece.uri, url), PIECE_TIMEOUT_MS);
                if (!current()) {
                    return;
                }
                this.#session.bytes += bytes.length;
                this.#enqueue(bytes, track, parameters);
                next = piece.part === undefined ? { msn: piece.msn + 1, part: undefined } : { msn: piece.msn, part: piece.part + 1 };
                if (behindLive(playlist, piece) * 1000 > (this.#options.maxBehindMs ?? 10_000)) {
                    this.#event('stalled', 'too far behind; back to live');
                    playlist = await this.#reload(url, playlist, undefined, current);
                    next = startPosition(playlist);
                    this.#clock = null;
                }
            }
        } catch (error) {
            if (current()) {
                this.#lose('error', text(error));
            }
        }
    }

    /** A piece's video samples, queued for their times. */
    #enqueue(bytes: Uint8Array, track: VideoTrack, parameters: { sps: Uint8Array | undefined; pps: Uint8Array | undefined }) : void {
        const receivedAt = wallNow();
        for (const sample of parseFragments(bytes, track)) {
            this.#queue.push({ unit: { ...accessUnitOf(sample, track, parameters), receivedAt }, decodeTime: sample.decodeTime });
        }
        if (this.#clock === null && this.#queue.length > 0) {
            this.#clock = { decodeTime: this.#queue[0]!.decodeTime, wall: monotonicNow(), timescale: track.timescale };
        }
        this.#pace();
    }

    /** Hands the pictures that are due to the decoder, and waits for the next (VH-5). */
    #pace() : void {
        clearTimeout(this.#paceTimer);
        const clock = this.#clock;
        if (!clock || !this.#pipeline || !this.#sessionActive) {
            return;
        }
        const now = monotonicNow();
        const dueAt = (decodeTime: number) : number => clock.wall + ((decodeTime - clock.decodeTime) * 1000) / clock.timescale;
        const due: AccessUnit[] = [];
        while (this.#queue.length > 0) {
            const first = this.#queue[0]!;
            const at = dueAt(first.decodeTime);
            // Much later than its time (the network held it up), or far ahead (the times jumped): the clock starts again here.
            if (now - at > RESYNC_MS || at - now > FAR_AHEAD_MS) {
                this.#clock = { ...clock, decodeTime: first.decodeTime, wall: now };
                this.#pipeline.pushUnits(due);
                this.#pace();

                return;
            }
            if (at > now) {
                break;
            }
            due.push(this.#queue.shift()!.unit);
        }
        if (due.length > 0) {
            this.#pipeline.pushUnits(due);
        }
        const following = this.#queue[0];
        if (following) {
            this.#paceTimer = setTimeout(() => this.#pace(), Math.max(0, dueAt(following.decodeTime) - now));
        }
    }

    #watch() : void {
        if (this.#state !== 'playing') {
            return;
        }

        const frames = this.#pipeline?.stats()?.decoder.framesDecoded ?? 0;
        const now = monotonicNow();
        if (frames > this.#framesNow) {
            if (this.#framesNow === 0) {
                this.#event('first-picture');
            }
            this.#framesNow = frames;
            this.#lastProgressAt = now;
            this.#retries = 0;
        } else if (now - this.#lastProgressAt >= this.#stallTimeoutMs) {
            this.#lose('stalled', `no picture for ${Math.round(this.#stallTimeoutMs / 1000)} s`);
        }
    }

    /** The attempt or session is over: clean up and schedule the next attempt. */
    #lose(type: 'stalled' | 'error' | 'closed', detail: string) : void {
        this.#generation++;
        if (type === 'stalled') {
            this.#stalls++;
        } else {
            this.#failures++;
        }
        this.#lastError = detail;
        this.#event(type, detail);
        this.#endSession();

        const base = this.#options.retryDelayMs ?? 2000;
        const delay = type === 'stalled' && this.#retries === 0 ? 0 : Math.min(base * 2 ** this.#retries, this.#options.maxRetryDelayMs ?? 15_000);
        this.#retries++;
        this.#state = 'waiting';
        this.#retryTimer = setTimeout(() => void this.#connect(), delay);
        this.#settleFirstOutcome();
    }

    #endSession() : void {
        clearTimeout(this.#paceTimer);
        this.#queue = [];
        this.#clock = null;
        if (!this.#sessionActive) {
            return;
        }

        this.#sessionActive = false;
        this.#framesNow = Math.max(this.#framesNow, this.#pipeline?.stats()?.decoder.framesDecoded ?? 0);
        this.#framesBefore += this.#framesNow;
        this.#framesNow = 0;
        // The canvas keeps the last picture until the next session paints over it.
        this.#pipeline?.end();
    }
}
